import React, { useId, useMemo, useState } from 'react';
import { PANELS, PANEL_HEIGHT } from '../panels/registry';
import { buildTonnetz, dampenLuminosity, noteToFifths } from '../utils/tonnetz';

// The Lux Tonnetz panel: a five-row hex grid that lights the played notes, laid out so
// harmonic relationships show as shapes, with the Harmonic Luminosity score of what is lit.
// The grid and score come from utils/tonnetz.ts; this file only draws them.

// The Chord Compass's background ramp: warm above the horizon, blue below, turning at 50%.
const HORIZON_GRADIENT: Array<[number, string]> = [
  [0, 'rgb(255, 255, 255)'],
  [15, 'rgb(255, 254, 214)'],
  [47.5, 'rgb(243, 197, 157)'],
  [50.5, 'rgb(107, 117, 142)'],
  [69, 'rgb(39, 49, 91)'],
  [100, 'rgb(0, 0, 0)'],
];

// The system UI font the header menus render in (San Francisco on macOS). Set on the note
// names directly rather than inherited, so the panel does not pick up the page's font.
const NOTE_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";

// How much larger the note names are laid out than drawn: enough to put the browser's idea of
// their size near what they actually measure on screen, tens of pixels rather than under one
const NOTE_TEXT_OVERSAMPLE = 50;

// Height of a capital in NOTE_FONT_FAMILY as a share of its font size (San Francisco's is 0.705)
const NOTE_CAP_HEIGHT = 0.705;

const signed = (value: number) => (value >= 0 ? `+${value.toFixed(2)}` : value.toFixed(2));

// Color mapping for rows
const ROW_COLORS: { inactive: Record<number, string>; active: Record<number, string> } = {
  inactive: {
    0: 'rgba(229, 114, 0, 0.5)',
    1: 'rgba(190, 152, 1, 0.5)',
    2: 'rgba(0, 0, 0, 0.5)',
    3: 'rgba(47, 69, 166, 0.5)',
    4: 'rgba(0, 107, 100, 0.5)'
  },
  active: {
    0: 'rgba(220, 145, 68, 0.95)',
    1: 'rgba(255, 223, 96, 0.95)',
    2: 'rgba(182, 182, 182, 0.95)',
    3: 'rgba(111, 128, 203, 0.95)',
    4: 'rgba(53, 160, 153, 0.95)'
  },
};
const KEY_CENTER_ACTIVE = '#FFFFFF';
const KEY_CENTER_INACTIVE = '#000000';


interface LuxTonnetzProps {
  /** Note at the center of row 2. Accepts `Bb` or `B♭`, `C#` or `C♯`. */
  keyCenter: string;
  /** Semitones above the key center to light. */
  activeScaleDegrees: number[];
  /** The inset shadow is lighter on the light theme, as on the Chord Compass. */
  appTheme: string;
  // How far the grey row reaches either side of the key center, in fifths. The sharp side
  // needs more room: E major (V/vi) sits at +8 fifths, so key-center mode reaches 8.
  fifthsLeft?: number;
  fifthsRight?: number;
  orientation?: 'horizontal' | 'rotated';
  // Everything inside is relative, since the grid is scaled to fit the panel: absolute
  // sizes would be scaled away. What survives is the grid's shape and these ratios.
  fontScale?: number;       // note name size as a share of the hex radius
  // 1.1 matches the rotated view's cell area, since a flat-top hex is wider for the same
  // radius; anything higher makes the horizontal cells the larger pair
  verticalStretch?: number;
  scoreTextScale?: number;  // luminosity line size as a share of the panel's width
  inactiveOpacity?: number; // how far the unlit cells fade back, note names included
  fadeSeconds?: number;     // how long a cell takes to go dark; coming on is instant
}

const LuxTonnetz: React.FC<LuxTonnetzProps> = ({
  keyCenter,
  activeScaleDegrees,
  appTheme,
  fifthsLeft = 7,
  fifthsRight = 8,
  orientation = 'rotated',
  fontScale = 0.88,
  verticalStretch = 1.1,
  scoreTextScale = 0.04,
  inactiveOpacity = 0.55,
  fadeSeconds = 0.3,
}) => {
  // The panel this has to fit, in dvw, from the registry so the two cannot disagree
  const panelWidth = PANELS.luxTonnetz.width;
  const panelHeight = PANEL_HEIGHT;

  // The hex radius is the unit everything else is measured in. Its value is arbitrary:
  // the panel scale below converts the finished grid into dvw.
  const hexRadius = 1;
  const fontSize = hexRadius * fontScale;
  const hexHeight = hexRadius * 2 * verticalStretch;
  const hexWidth = Math.sqrt(3) * hexRadius;
  const horizontalGap = hexRadius * 0.1;
  const verticalGap = hexRadius * 0.1;
  const verticalSpacing = (hexHeight * 0.75) + verticalGap;

  // Column of the key center in row 2, which is also its distance from the left edge in fifths
  const midway = fifthsLeft;
  const numColumns = fifthsLeft + fifthsRight + 1;
  // Puts the key center at row 2, column `midway`
  const startValue = 7 - midway;

  let shift = noteToFifths(keyCenter);
  if (shift === null) {
    console.warn(`Unknown key center "${keyCenter}", falling back to C`);
    shift = 0;
  }

  const { noteMatrix, activeMatrix, luminosity: rawLuminosity } = useMemo(
    () => buildTonnetz({ numColumns, startValue, shift: shift as number, midway, activeScaleDegrees }),
    [numColumns, startValue, shift, midway, activeScaleDegrees]
  );
  const luminosity = dampenLuminosity(rawLuminosity, activeScaleDegrees.length);

  // The panel's own toggle owns the view; the prop sets which one it starts on
  const [rotatedView, setRotatedView] = useState(orientation === 'rotated');
  const isRotated = rotatedView;

  // Unique per instance: a filter id would otherwise be resolved to whichever SVG on the
  // page defined it first
  const instanceId = useId().replace(/:/g, '');
  const glowId = `glow-${instanceId}`;

  // Rotated layout: regular flat-top hexes, the horizontal layout turned 30° counter-clockwise.
  // Major thirds stack straight up; each fifth steps up-right at 30°.
  const rotatedRadius = hexRadius * 1.1;
  const rotatedGap = hexRadius * 0.1;
  const rotatedStepX = 1.5 * (rotatedRadius + rotatedGap / 2);
  const rotatedStepY = Math.sqrt(3) * (rotatedRadius + rotatedGap / 2);
  // Fifths-axis offset of column 0 in each row from the key center, for midway = 7.
  // A row's third count (2 - rowIndex) accounts for the rest of each cell's line-of-fifths distance.
  const rotatedFifthsOffset = [-8, -8, -7, -7, -6];

  const cellCenter = (rowIndex: number, colIndex: number) => {
    if (isRotated) {
      const thirds = 2 - rowIndex;
      const fifths = colIndex - midway + rotatedFifthsOffset[rowIndex] + 7;
      return {
        x: fifths * rotatedStepX,
        y: -thirds * rotatedStepY - fifths * rotatedStepY / 2,
      };
    }
    const xOffset = rowIndex % 2 === 0 ? (hexWidth + horizontalGap) / 2 : 0;
    return {
      x: colIndex * (hexWidth + horizontalGap) + (hexWidth + horizontalGap) / 2 + xOffset,
      y: rowIndex * verticalSpacing + hexHeight / 2,
    };
  };

  const calculateHexPoints = (centerX: number, centerY: number) => {
    const points: string[] = [];
    for (let i = 0; i < 6; i++) {
      if (isRotated) {
        const angleRad = (Math.PI / 180) * (60 * i);
        points.push(`${centerX + rotatedRadius * Math.cos(angleRad)}`, `${centerY + rotatedRadius * Math.sin(angleRad)}`);
        continue;
      }
      // Drawn at full radius: the gaps already live in the cell pitch, which carries
      // horizontalGap and verticalGap
      const angleRad = (Math.PI / 180) * (60 * i - 30);
      const x = centerX + hexRadius * Math.cos(angleRad);
      const y = centerY + hexRadius * verticalStretch * Math.sin(angleRad);
      points.push(`${x}`, `${y}`);
    }
    return points.join(' ');
  };

  // Grid bounds in grid units
  let gridMinX: number;
  let gridMinY: number;
  let gridWidth: number;
  let gridHeight: number;
  if (isRotated) {
    const centers = noteMatrix.flatMap((row, r) => row.map((_, c) => cellCenter(r, c)));
    const padding = hexRadius * 0.4;
    gridMinX = Math.min(...centers.map(p => p.x)) - rotatedRadius - padding;
    gridMinY = Math.min(...centers.map(p => p.y)) - rotatedStepY / 2 - padding;
    gridWidth = Math.max(...centers.map(p => p.x)) + rotatedRadius + padding - gridMinX;
    gridHeight = Math.max(...centers.map(p => p.y)) + rotatedStepY / 2 + padding - gridMinY;
  } else {
    const padding = hexRadius * 0.25;
    gridMinX = -padding;
    gridMinY = -padding;
    // numColumns + 0.5, since the offset rows only overhang by half a cell
    gridWidth = (hexWidth + horizontalGap) * (numColumns + 0.5) + padding * 2;
    gridHeight = verticalSpacing * 6 + padding * 2;
  }

  // The horizon runs along the whole-tone axis through the key center: C, B♭, A♭ one way,
  // D, E the other. Rotated, that axis is level. In the horizontal layout each whole tone
  // is one row down and two columns right — a half-column further because the rows
  // alternate offsets — so the line drops one row per 1.5 columns. Taller hexes
  // (verticalStretch) push the rows apart and steepen it.
  const horizon = cellCenter(2, midway);
  const horizonSlope = isRotated ? 0 : verticalSpacing / (1.5 * (hexWidth + horizontalGap));

  // The SVG fills the whole panel, rather than shrinking to the grid, so the background
  // reaches every edge in both orientations. The grid is centered across the panel, but
  // vertically it is the key center that sits at the middle, so the horizon lands at the
  // same height as the Chord Compass's. The grid is not symmetric about the key center, so
  // the fit allows for the taller of its two halves.
  const gridHalfHeight = Math.max(horizon.y - gridMinY, gridMinY + gridHeight - horizon.y);
  const scale = Math.min(panelWidth / gridWidth, panelHeight / (gridHalfHeight * 2)); // dvw per grid unit
  const viewWidth = panelWidth / scale;
  const viewHeight = panelHeight / scale;
  const minX = gridMinX + gridWidth / 2 - viewWidth / 2;
  const minY = horizon.y - viewHeight / 2;
  const viewBox = `${minX} ${minY} ${viewWidth} ${viewHeight}`;

  // The Chord Compass's ramp, run across the horizon instead of straight down the panel.
  // The gradient axis is the horizon's perpendicular, centered on the horizon and long
  // enough to cover the furthest corner, so the ramp's sharp warm-to-blue turn lands
  // exactly on the line.
  const perpendicularLength = Math.hypot(horizonSlope, 1);
  const perpendicular = { x: -horizonSlope / perpendicularLength, y: 1 / perpendicularLength };
  const distanceBelowHorizon = (point: { x: number; y: number }) =>
    (point.x - horizon.x) * perpendicular.x + (point.y - horizon.y) * perpendicular.y;
  // Measured across the grid, not the panel. The turn from warm to blue is only 3% of the
  // ramp's length, so running it the full height of the panel smears that turn wide. Beyond
  // the ends the gradient holds its last colour, which fills the rest of the panel.
  const gridCorners = [
    { x: gridMinX, y: gridMinY },
    { x: gridMinX + gridWidth, y: gridMinY },
    { x: gridMinX, y: gridMinY + gridHeight },
    { x: gridMinX + gridWidth, y: gridMinY + gridHeight },
  ];
  const halfSpan = Math.max(...gridCorners.map(corner => Math.abs(distanceBelowHorizon(corner))));

  // That ramp as a CSS background on the panel, like the Compass's, so the Compass's inset
  // shadow can sit on it the same way: above the background, below the hexes. The viewBox
  // maps onto the panel without distortion, so its units stand in for the panel's.
  // A CSS gradient line runs through the box's center at the gradient's angle, and is just
  // long enough for its ends to touch the far corners; stops are placed along it.
  const rampAngle = Math.atan2(perpendicular.x, -perpendicular.y); // CSS: 0 points up, clockwise
  const rampLineLength =
    Math.abs(viewWidth * Math.sin(rampAngle)) + Math.abs(viewHeight * Math.cos(rampAngle));
  const viewCenter = { x: minX + viewWidth / 2, y: minY + viewHeight / 2 };
  // Where the ramp's white end falls along the gradient line, measured from the line's start
  const rampStart = distanceBelowHorizon(viewCenter) * -1 - halfSpan + rampLineLength / 2;
  const rampStops = HORIZON_GRADIENT.map(([offset, color]) => {
    const along = rampStart + (offset / 100) * halfSpan * 2;
    return `${color} ${(along / rampLineLength) * 100}%`;
  });
  const horizonBackground = `linear-gradient(${(rampAngle * 180) / Math.PI}deg, ${rampStops.join(', ')})`;

  // The score sits in the panel's top-left corner, four short lines in a black box so the
  // white and coloured text reads the same whichever end of the ramp is behind it.
  const scoreTextSize = (panelWidth * scoreTextScale * 0.58) / scale;
  const scoreLineHeight = scoreTextSize * 1.4;
  const scorePadding = scoreTextSize * 0.7;
  const scoreBoxX = minX + scoreTextSize * 0.8;
  const scoreBoxY = minY + scoreTextSize * 0.8;
  const scoreX = scoreBoxX + scorePadding;
  const scoreY = scoreBoxY + scorePadding + scoreTextSize;
  const scoreLines = [
    { label: 'luminosity', value: signed(luminosity.net), color: luminosity.net >= 0 ? ROW_COLORS.active[1] : ROW_COLORS.active[3], emphasis: 1.25 },
    { label: 'bright', value: signed(luminosity.bright), color: ROW_COLORS.active[1], emphasis: 1 },
    { label: 'dark', value: signed(luminosity.dark), color: ROW_COLORS.active[3], emphasis: 1 },
    { label: 'contrast', value: luminosity.contrast.toFixed(2), color: '#d8d8d8', emphasis: 1 },
  ];

  // Roughly 0.55em per character in this font, enough to size the box around the longest line
  const scoreBoxWidth = scorePadding * 2 + Math.max(
    ...scoreLines.map(line => `${line.label} ${line.value}`.length * scoreTextSize * line.emphasis * 0.55)
  );
  const scoreBoxHeight = scorePadding * 2 + scoreLineHeight * scoreLines.length;

  // The view toggle, top-right: a glyph for the view you are looking at — a slash for the
  // rotated grid climbing at an angle, a dash for the horizontal one
  const toggleSize = scoreTextSize * 2.4;
  const toggleX = minX + viewWidth - toggleSize - scoreTextSize * 0.8;
  const toggleY = minY + scoreTextSize * 0.8;
  const toggleGlyph = isRotated ? '∕' : '–';
  const toggleColor = ROW_COLORS.active[2];

  return (
    <div
      style={{
        width: `${panelWidth}dvw`,
        height: `${panelHeight}dvw`,
        backgroundColor: 'black',
        backgroundImage: horizonBackground,
        boxShadow: `inset 0dvw 0dvw 5dvw 0.5dvw rgba(27, 20, 13, ${appTheme === 'light' ? 0.6 : 0.99})`,
        overflow: 'hidden',
        borderRadius: '1dvw',
        // The hairline every panel has; the Chord Compass gets it from App.css .pie-chart
        border: '0.05dvw solid #555',
      }}
    >
      <svg
        viewBox={viewBox}
        style={{
          // Width and height must be set here, not as attributes: App.css has a global
          // `svg { width: 100%; height: 37dvw }` that outranks presentation attributes.
          // 100% rather than the panel's size, which would run under the border.
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      >
        <defs>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={hexRadius * 0.15} result="blur1" />
            <feColorMatrix
              in="blur1"
              type="matrix"
              values="1 0 0 0 1   1 0 0 0 1   1 0 0 0 1  0 0 0 1 0"
              result="whiteBlur"
            />
            <feGaussianBlur stdDeviation={hexRadius * 0.075} result="blur2" />
            <feMerge>
              <feMergeNode in="whiteBlur" />
              <feMergeNode in="blur2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {noteMatrix.map((row, rowIndex) =>
          row.map((note, colIndex) => {
            const { x, y } = cellCenter(rowIndex, colIndex);

            const active = activeMatrix[rowIndex][colIndex];
            const isKeyCenter = rowIndex === 2 && colIndex === midway;

            // Picked by lookup rather than nested ternaries, which this React version bans
            const state = `${isKeyCenter ? 'keyCenter' : 'cell'}-${active ? 'active' : 'inactive'}`;
            const fill = ({
              'keyCenter-active': KEY_CENTER_ACTIVE,
              'keyCenter-inactive': KEY_CENTER_INACTIVE,
              'cell-active': ROW_COLORS.active[rowIndex],
              'cell-inactive': ROW_COLORS.inactive[rowIndex],
            } as Record<string, string>)[state];
            const stroke = ({
              'keyCenter-active': '#000000',
              'keyCenter-inactive': '#111111',
              'cell-active': '#ffffff',
              'cell-inactive': '#404040',
            } as Record<string, string>)[state];
            const fontWeight = ({
              // The tonic's larger label reads heavier at the same weight, so it steps down
              'keyCenter-active': 600,
              'keyCenter-inactive': 500,
              'cell-active': 700,
              'cell-inactive': 500,
            } as Record<string, number>)[state];
            const strokeScale = { keyCenter: 0.15, active: 0.1, inactive: 0.05 };
            let strokeKey: 'keyCenter' | 'active' | 'inactive' = 'inactive';
            if (isKeyCenter) strokeKey = 'keyCenter';
            else if (active) strokeKey = 'active';

            // Dimming the whole cell, hex and note name together, lets the background
            // through and leaves the lit ones to carry the shape. The key center keeps
            // full strength either way: it is the anchor, lit or not.
            const cellOpacity = active || isKeyCenter ? 1 : inactiveOpacity;

            // Lighting up is instant; going dark eases out over fadeSeconds — hence no
            // transition while active.
            const points = calculateHexPoints(x, y);
            const fadeOut = active ? 'none' : `fill ${fadeSeconds}s ease-out`;

            return (
              <g key={`${rowIndex}-${colIndex}`} opacity={cellOpacity}>
                {/* The glow is its own copy underneath, so it can fade on its own timing */}
                <polygon
                  points={points}
                  fill={fill}
                  stroke="#ffffff"
                  strokeWidth={hexRadius * strokeScale.active}
                  strokeLinejoin="round"
                  filter={`url(#${glowId})`}
                  style={{
                    opacity: active ? 1 : 0,
                    transition: active ? 'none' : `opacity ${fadeSeconds}s ease-out`,
                  }}
                />
                <polygon
                  points={points}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={hexRadius * strokeScale[strokeKey]}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{ transition: fadeOut, isolation: 'isolate', paintOrder: 'stroke fill' }}
                />
                {/* Laid out NOTE_TEXT_OVERSAMPLE times larger, then scaled back down. At its
                    real size of under 1px, the system font applies the loose letter spacing
                    meant for tiny text, which the viewBox then enlarges. */}
                <text
                  x={x * NOTE_TEXT_OVERSAMPLE}
                  y={y * NOTE_TEXT_OVERSAMPLE}
                  transform={`scale(${1 / NOTE_TEXT_OVERSAMPLE})`}
                  textAnchor="middle"
                  // Centers the capital letter itself: the baseline goes half a cap height
                  // below the cell center. 'central' sat low and 'middle' sat high.
                  dominantBaseline="alphabetic"
                  dy={`${NOTE_CAP_HEIGHT / 2}em`}
                  textRendering="geometricPrecision"
                  fill={active ? '#000000' : '#ffffff'}
                  fontFamily={NOTE_FONT_FAMILY}
                  fontWeight={fontWeight}
                  style={{
                    fontSize: (isKeyCenter ? fontSize * 1.4 : fontSize) * NOTE_TEXT_OVERSAMPLE,
                    transition: fadeOut,
                    userSelect: 'none',
                  }}
                >
                  {note}
                </text>
              </g>
            );
          })
        )}

        <g>
          <rect
            x={scoreBoxX}
            y={scoreBoxY}
            width={scoreBoxWidth}
            height={scoreBoxHeight}
            rx={scoreTextSize * 0.4}
            fill="rgba(0, 0, 0, 0.82)"
          />
          {scoreLines.map((line, index) => (
            <text
              key={line.label}
              x={scoreX}
              y={scoreY + index * scoreLineHeight}
              fontSize={scoreTextSize * line.emphasis}
              style={{ userSelect: 'none' }}
            >
              <tspan fill="#c9c9c9">{line.label} </tspan>
              <tspan fill={line.color} fontWeight={600}>{line.value}</tspan>
            </text>
          ))}
        </g>

        <g
          onClick={() => setRotatedView(!rotatedView)}
          style={{ cursor: 'pointer' }}
          role="button"
          aria-label={isRotated ? 'Rotated view; switch to horizontal' : 'Horizontal view; switch to rotated'}
        >
          <rect
            x={toggleX}
            y={toggleY}
            width={toggleSize}
            height={toggleSize}
            rx={scoreTextSize * 0.4}
            fill="rgba(0, 0, 0, 0.82)"
            stroke={toggleColor}
            strokeWidth={scoreTextSize * 0.1}
          />
          <text
            x={toggleX + toggleSize / 2}
            y={toggleY + toggleSize / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fill={toggleColor}
            fontSize={toggleSize * 0.7}
            fontWeight={600}
            style={{ userSelect: 'none' }}
          >
            {toggleGlyph}
          </text>
        </g>
      </svg>
    </div>
  );
};

export default LuxTonnetz;
